import { useCallback, useRef, useState } from 'react';
import {
  DndContext,
  type DragEndEvent,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { InventoryImageInput } from '@ncc/shared';
import { uploadFiles } from '../lib/uploader';

type WithKey = InventoryImageInput & { key: string };

function withKey(img: InventoryImageInput, i: number): WithKey {
  return { ...img, key: img.image_id ?? `${img.url}#${i}` };
}

type Props = {
  initial: InventoryImageInput[];
  onChange: (next: InventoryImageInput[]) => void;
};

export function ImageManager({ initial, onChange }: Props) {
  const [items, setItems] = useState<WithKey[]>(() => initial.map((img, i) => withKey(img, i)));
  const [uploading, setUploading] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragOverRef = useRef(false);
  const [dragOver, setDragOver] = useState(false);

  const sensors = useSensors(
    // Mouse: a tiny threshold so a plain click still reads as a click, not a drag.
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    // Touch: press-and-hold to begin a drag, so ordinary finger-scrolling over
    // the image grid keeps working on phones and tablets.
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
  );

  const persist = useCallback(
    (next: WithKey[]) => {
      setItems(next);
      onChange(next.map(({ key: _key, ...rest }, i) => ({ ...rest, position: i })));
    },
    [onChange],
  );

  async function addFiles(files: FileList | File[]) {
    const list = Array.from(files);
    if (!list.length) return;
    setError(null);
    setUploading({ done: 0, total: list.length });
    try {
      const uploaded = await uploadFiles(list, (done, total) => setUploading({ done, total }));
      const next = [
        ...items,
        ...uploaded.map((u, i) =>
          withKey({ url: u.url, image_id: u.image_id, position: items.length + i }, items.length + i),
        ),
      ];
      persist(next);
    } catch (e) {
      setError(String(e));
    } finally {
      setUploading(null);
    }
  }

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.key === active.id);
    const newIndex = items.findIndex((i) => i.key === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    persist(arrayMove(items, oldIndex, newIndex));
  }

  function remove(key: string) {
    persist(items.filter((i) => i.key !== key));
  }

  return (
    <div>
      <div
        className={`dropzone ${dragOver ? 'is-over' : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!dragOverRef.current) {
            dragOverRef.current = true;
            setDragOver(true);
          }
        }}
        onDragLeave={() => {
          dragOverRef.current = false;
          setDragOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          dragOverRef.current = false;
          setDragOver(false);
          if (e.dataTransfer.files.length > 0) void addFiles(e.dataTransfer.files);
        }}
        style={{ marginBottom: '1rem' }}
      >
        <strong>Drag & drop photos here, or click to choose.</strong>
        <div style={{ fontSize: '0.85rem', marginTop: 4 }}>
          First image is the thumbnail. Drag — or press &amp; hold on touch — to reorder.
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files) void addFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {uploading ? (
        <div style={{ marginBottom: '0.75rem', color: 'var(--muted)' }}>
          Uploading {uploading.done}/{uploading.total}…
        </div>
      ) : null}
      {error ? <div className="error-text" style={{ marginBottom: '0.75rem' }}>{error}</div> : null}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={items.map((i) => i.key)} strategy={rectSortingStrategy}>
          <div className="image-list">
            {items.map((item, i) => (
              <SortableTile key={item.key} item={item} index={i} onRemove={() => remove(item.key)} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function SortableTile({
  item,
  index,
  onRemove,
}: {
  item: WithKey;
  index: number;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.key,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundImage: `url(${item.url})`,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`image-tile ${isDragging ? 'is-dragging' : ''}`}
    >
      <button
        type="button"
        className="remove"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        aria-label="Remove"
      >
        ×
      </button>
      <span className="position">{index + 1}</span>
    </div>
  );
}
